import styles from './PlatformCard.module.css'

export default function PlatformCard({ platform, onOpenModal, reverse = false }) {
  const isIntern   = platform.id === 'internnexus'
  const isPrompt   = platform.id === 'promptnexus'
  const checkClass = isIntern ? 'check-cyan' : isPrompt ? 'check-orange' : 'check-indigo'

  return (
    <article
      id={platform.id}
      className={styles.card}
      aria-label={`${platform.name} platform`}
    >
      <div className={`${styles.grid} ${reverse ? styles.reverse : ''}`}>
        {/* ── LEFT / INFO SIDE ── */}
        <div className={styles.info}>
          <span
            className={`platform-badge ${styles.badge}`}
            style={{
              background: platform.colorBg,
              border: `1px solid ${platform.colorBorder}`,
              color: platform.color,
            }}
          >
            <span className={styles.badgeDot} style={{ background: platform.dotColor }} />
            {platform.tagline}
          </span>

          <h2 className={styles.title}>{platform.name}</h2>
          <p className={styles.desc}>{platform.description}</p>

          <ul className="feature-list">
            {platform.features.map(f => (
              <li key={f}>
                <span className={`check-dot ${checkClass}`} />
                {f}
              </li>
            ))}
          </ul>

          <div className={styles.actions}>
            {platform.id !== 'promptnexus' && onOpenModal ? (
              <button
                className={`btn ${isIntern ? 'btn-cyan' : 'btn-primary'}`}
                onClick={() => onOpenModal(platform.id)}
              >
                {platform.cta.primary}
              </button>
            ) : (
              <a
                href={platform.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                {platform.cta.primary}
              </a>
            )}
            <a
              href={platform.url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-ghost"
            >
              {platform.cta.secondary}
            </a>
          </div>
        </div>

        {/* ── RIGHT / VISUAL SIDE ── */}
        <div className={styles.visual}>
          {isIntern ? (
            <HowItWorksPanel platform={platform} />
          ) : isPrompt ? (
            <ChallengeTypesPanel platform={platform} />
          ) : (
            <SkillStatsPanel platform={platform} />
          )}
        </div>
      </div>
    </article>
  )
}

/* ── SkillNexus stats panel ── */
function SkillStatsPanel({ platform }) {
  return (
    <div className={styles.statsPanel}>
      <p className={styles.panelLabel}>Platform Highlights</p>
      <div className={styles.statsGrid}>
        {platform.stats.map(({ val, label }) => (
          <div key={label} className={styles.statChip}>
            <p className={styles.statVal} style={{ color: platform.color }}>{val}</p>
            <p className={styles.statLabel}>{label}</p>
          </div>
        ))}
      </div>
      <div className={styles.levelsBox}>
        <p className={styles.levelsTitle}>Learning Levels</p>
        <div className={styles.levelTags}>
          {['Beginner', 'Intermediate', 'Advanced'].map(l => (
            <span
              key={l}
              className={styles.levelTag}
              style={{
                background: `${platform.colorBg}`,
                border: `1px solid ${platform.colorBorder}`,
                color: platform.color,
              }}
            >
              {l}
            </span>
          ))}
          <span className={styles.hotTag}>🔥 HOT</span>
        </div>
      </div>
    </div>
  )
}

/* ── InternNexus how-it-works panel ── */
function HowItWorksPanel({ platform }) {
  return (
    <div className={styles.statsPanel}>
      <p className={styles.panelLabel}>How It Works</p>
      <div className={styles.howBox}>
        {platform.howItWorks.map((step, i) => (
          <div
            key={i}
            className={styles.howRow}
            style={{ borderBottom: i < platform.howItWorks.length - 1 ? '1px solid var(--border)' : 'none' }}
          >
            <div
              className={styles.howNum}
              style={{ background: platform.colorBg, color: platform.color, border: `1px solid ${platform.colorBorder}` }}
            >
              {i + 1}
            </div>
            <div>
              <p className={styles.howTitle}>{step.title}</p>
              <p className={styles.howSub}>{step.sub}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ── PromptNexus challenge types panel ── */
function ChallengeTypesPanel({ platform }) {
  return (
    <div className={styles.statsPanel} style={{ background: 'linear-gradient(135deg,rgba(232,100,42,0.06),rgba(28,45,94,0.08))' }}>
      <p className={styles.panelLabel}>Challenge Types</p>
      <div className={styles.challengeList}>
        {platform.challengeTypes.map(({ icon, title, sub, bg }) => (
          <div key={title} className={styles.challengeItem}>
            <div className={styles.challengeIcon} style={{ background: bg }}>{icon}</div>
            <div>
              <p className={styles.challengeTitle}>{title}</p>
              <p className={styles.challengeSub}>{sub}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
